import { FC } from 'react';

import { Minus, Plus, RotateCcw, Trash2 } from 'lucide-react';

import { OrderEditableOffer } from './OrderOfferSearch';
import { formatOrderAmount } from './orderRevisionUtils';
import { Button } from '../../../components/ui/Button';
import { Image } from '../../../components/ui/Image';
import { Order } from '../../../types';

export type ExistingOrderEditDraft = {
    order_item_id: number;
    offer_id: number;
    quantity: number;
    removed: boolean;
};

export type AddedOrderEditDraft = {
    offer: OrderEditableOffer;
    quantity: number;
};

type OrderItem = NonNullable<Order['order_items']>[number];

interface Props {
    items: ExistingOrderEditDraft[];
    addedItems: AddedOrderEditDraft[];
    orderItemsById: Map<number, OrderItem>;
    onToggleRemoved: (orderItemId: number) => void;
    onExistingQuantityChange: (orderItemId: number, quantity: number) => void;
    onRemoveAdded: (offerId: number) => void;
    onAddedQuantityChange: (offerId: number, quantity: number) => void;
}

const QuantityControl: FC<{
    quantity: number;
    maximum: number;
    onChange: (quantity: number) => void;
}> = ({ quantity, maximum, onChange }) => (
    <div className="flex items-center rounded-xl border">
        <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-9 w-9 rounded-r-none"
            disabled={quantity <= 1}
            aria-label="Зменшити кількість"
            onClick={() => onChange(quantity - 1)}
        >
            <Minus className="h-4 w-4" />
        </Button>
        <span className="w-10 text-center text-sm font-semibold">
            {quantity}
        </span>
        <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-9 w-9 rounded-l-none"
            disabled={quantity >= maximum}
            aria-label="Збільшити кількість"
            onClick={() => onChange(quantity + 1)}
        >
            <Plus className="h-4 w-4" />
        </Button>
    </div>
);

export const BuyerOrderEditItems: FC<Props> = ({
    items,
    addedItems,
    orderItemsById,
    onToggleRemoved,
    onExistingQuantityChange,
    onRemoveAdded,
    onAddedQuantityChange,
}) => (
    <section className="space-y-3">
        <h3 className="text-sm font-semibold">Товари</h3>
        {items.map((draft) => {
            const item = orderItemsById.get(draft.order_item_id);
            if (!item) return null;

            return (
                <div
                    key={draft.order_item_id}
                    className={`flex gap-3 rounded-xl border p-3 ${draft.removed ? 'bg-destructive/5 opacity-70' : ''}`}
                >
                    <div className="h-20 w-14 flex-shrink-0 overflow-hidden rounded bg-muted">
                        <Image
                            src={item.product?.image || '/placeholder.svg'}
                            alt={item.product?.name}
                            className="object-cover"
                        />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <p className="font-medium">
                                    {item.product?.name}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    {formatOrderAmount(item.price_per_item)} /
                                    шт.
                                </p>
                            </div>
                            <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-destructive"
                                aria-label={
                                    draft.removed
                                        ? 'Повернути товар'
                                        : 'Видалити товар'
                                }
                                onClick={() =>
                                    onToggleRemoved(draft.order_item_id)
                                }
                            >
                                {draft.removed ? (
                                    <RotateCcw className="h-4 w-4" />
                                ) : (
                                    <Trash2 className="h-4 w-4" />
                                )}
                            </Button>
                        </div>
                        {!draft.removed && (
                            <div className="mt-3">
                                <QuantityControl
                                    quantity={draft.quantity}
                                    maximum={
                                        item.maximum_revision_quantity ??
                                        item.amount
                                    }
                                    onChange={(quantity) =>
                                        onExistingQuantityChange(
                                            draft.order_item_id,
                                            quantity,
                                        )
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>
            );
        })}

        {addedItems.map((draft) => (
            <div
                key={draft.offer.id}
                className="flex gap-3 rounded-xl border border-primary/40 bg-primary/5 p-3"
            >
                <div className="h-20 w-14 flex-shrink-0 overflow-hidden rounded bg-muted">
                    <Image
                        src={draft.offer.product?.image || '/placeholder.svg'}
                        alt={draft.offer.product?.name}
                        className="object-cover"
                    />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <p className="font-medium">
                                {draft.offer.product?.name}
                            </p>
                            <p className="text-sm text-muted-foreground">
                                {formatOrderAmount(draft.offer.price)} / шт. ·
                                новий товар
                            </p>
                        </div>
                        <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive"
                            aria-label="Прибрати доданий товар"
                            onClick={() => onRemoveAdded(draft.offer.id)}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                    <div className="mt-3">
                        <QuantityControl
                            quantity={draft.quantity}
                            maximum={draft.offer.amount}
                            onChange={(quantity) =>
                                onAddedQuantityChange(draft.offer.id, quantity)
                            }
                        />
                    </div>
                </div>
            </div>
        ))}
    </section>
);
